import {
  SiPostgresql,
  SiNodedotjs,
  SiReact,
  SiTailwindcss,
  SiJavascript,
  SiGit,
  SiGithub,
  SiGmail,
  SiPython,
  SiPytorch,
  SiFastapi,
  SiLangchain,
} from "react-icons/si";
import { FaLinkedin } from "react-icons/fa";

export const locales = ["en", "de"];

export const technologies = [
  { name: "PostgreSQL", icon: SiPostgresql },
  { name: "Node.js", icon: SiNodedotjs },
  { name: "React", icon: SiReact },
  { name: "Tailwind CSS", icon: SiTailwindcss },
  { name: "JavaScript", icon: SiJavascript },
  { name: "Git", icon: SiGit },
  { name: "Python", icon: SiPython },
  { name: "PyTorch", icon: SiPytorch },
  { name: "FastAPI", icon: SiFastapi },
  { name: "LangChain", icon: SiLangchain },
];

const email = "job@alexandrefernandes.dev";

export const socialLinks = [
  { icon: SiGithub, link: "https://github.com/AlexPT2k22", label: "GitHub" },
  {
    icon: FaLinkedin,
    link: "https://www.linkedin.com/in/alexandrerodrifernandes/",
    label: "LinkedIn",
  },
  { icon: SiGmail, link: `mailto:${email}`, label: "Email" },
];

// Impressum details (§ 5 DDG). The law requires a full postal address where you can
// receive mail (no P.O. box): fill in street and postalCode before relying on the page.
export const legalInfo = {
  name: "Alexandre Rodrigues Fernandes",
  street: null,
  postalCode: null,
  city: "Schleswig",
  email,
};

export const content = {
  en: {
    meta: {
      title: "Alexandre Fernandes — AI/ML & Full Stack Software Engineer",
      description:
        "AI/ML Software Engineer near Hamburg. I build RAG, Text-to-SQL and LLM systems with Python, LangChain and FastAPI. Open to full-time roles in Germany.",
    },
    og: {
      alt: "Alexandre Fernandes — AI/ML Software Engineer",
      eyebrow: "alexandrefernandes.dev",
      title: "Alexandre Fernandes",
      subtitle: "AI/ML Software Engineer · RAG, LLMs & Full Stack",
      footer: "Hamburg area, Germany",
      projectEyebrow: "Case study · Alexandre Fernandes",
    },
    landing: { role: "Software Engineer", cta: "See Portfolio" },
    nav: [
      { id: "about-me", label: "About Me" },
      { id: "experience", label: "Experience" },
      { id: "projects", label: "Projects" },
      { id: "contact", label: "Contact" },
    ],
    languageSwitch: {
      href: "/de",
      hrefLang: "de",
      label: "DE",
      ariaLabel: "Diese Seite auf Deutsch",
    },
    hero: {
      role: "Software Engineer | AI/ML & Full Stack",
      intro:
        "Building LLM-powered systems and full-stack applications. Based near Hamburg and open to AI/ML roles.",
      viewProjects: "View Projects",
      getInTouch: "Get in Touch",
      scroll: "Scroll to explore",
      scrollLabel: "Scroll down to explore",
    },
    technologies: { heading: "Technologies" },
    about: {
      heading: "About Me",
      lead: "I build AI-powered systems and full-stack applications, from LLM pipelines to production web apps.",
      paragraphs: [
        "I'm a software engineer focused on AI/ML. During my internship at Geodouro I built a fully local RAG + Text-to-SQL assistant over 3 PostgreSQL databases with 500+ tables, taking answer accuracy from 13% to 77% with Qwen2.5 7B, hybrid retrieval and an LLM-as-Judge evaluation loop.",
        "My projects span computer vision (CNN + ALPR for smart parking), LLM and voice systems, and full-stack platforms used by real teams. I pick the right tool for the problem: Python for AI/ML, React + Node.js for web, FastAPI for APIs.",
        "I graduated in Computer Engineering from ESTGV/IPV in Portugal in June 2026 and now live in Schleswig, within commuting distance of Hamburg. I'm an EU citizen looking for a full-time Software Engineer (AI/ML) role. Meanwhile I'm building Zyllo, an LLM-driven SaaS for e-commerce, and learning German (A1).",
      ],
      resume: "Download Resume",
      resumeLabel: "Download resume as PDF",
    },
    stats: [
      { label: "Projects", value: "5+" },
      { label: "Technologies", value: "10+" },
      { label: "Experience", value: "2 years" },
      { label: "Learning", value: "Always" },
    ],
    experience: {
      heading: "Experience & Education",
      caseStudy: "Read the case study",
      items: [
        {
          title: "AI Engineer Intern — Geodouro",
          period: "Feb 2026 – Jun 2026",
          description:
            "Built a production RAG + Text-to-SQL assistant for SIGP, Geodouro's agricultural management platform. Replaced a free-roaming ReAct agent with a controlled pipeline (hybrid BM25 + MMR retrieval, schema pruning, dynamic few-shot, SQL validation with self-correction) over 3 PostgreSQL databases with 500+ tables. Accuracy on the company's golden set rose from 13% to 77%, with Qwen2.5 7B running fully locally and beating a 14B model.",
          tech: ["Python", "LangChain", "LangGraph", "FastAPI", "ChromaDB", "Qwen2.5", "PostgreSQL", "Langfuse"],
          slug: "rag-sql-chatbot",
        },
        {
          title: "BSc Computer Engineering — ESTGV, Polytechnic Institute of Viseu",
          period: "Oct 2023 – Jun 2026",
          description:
            "Graduated in June 2026 with a final grade of 15/20 (German 2.5). Final internship and project graded 20/20 (German 1.0).",
          tech: [],
        },
      ],
    },
    projects: {
      heading: "Projects",
      caseStudy: "Read the case study",
      inProgress: "In progress",
      githubLabel: (title) => `View ${title} on GitHub`,
    },
    projectPage: {
      back: "All projects",
      problem: "Problem",
      solution: "Solution",
      stack: "Tech stack",
      results: "Results",
      repo: "Source code",
      demo: "Live demo",
      report: "Project report (PDF, Portuguese)",
      more: "More projects",
      ctaTitle: "Interested in working together?",
      ctaText: "I'm open to full-time Software Engineer (AI/ML) roles in Hamburg or remote.",
      ctaButton: "Get in touch",
      breadcrumbHome: "Home",
    },
    contact: {
      heading: "Let's Connect",
      subtitle: "Have a role, a project or a question? Get in touch!",
      name: "Name",
      nameLabel: "Your name",
      email: "Email",
      emailLabel: "Your email address",
      message: "Message",
      messageLabel: "Your message",
      send: "Send Message",
      sending: "Sending...",
      success: "Message sent successfully!",
      error: "Failed to send message. Please try again.",
      privacyNote: "Your details are only used to answer your message. Details in the",
      privacyLink: "privacy policy",
      connect: "Connect With Me",
      locationLabel: "Location:",
      location: "Schleswig, Germany · commutable to Hamburg",
      statusLabel: "Status:",
      status: "Open to Software Engineer (AI/ML) roles · Full-time · Available now",
    },
    footer: {
      role: "Software Engineer | AI/ML & Full Stack",
      navLabel: "Footer navigation",
      about: "About",
      projects: "Projects",
      contact: "Contact",
      rights: "All rights reserved.",
      legal: "Legal notice",
      privacy: "Privacy",
      backToTop: "Back to Top",
      backToTopLabel: "Scroll back to top of page",
    },
    legal: {
      title: "Legal Notice (Impressum)",
      description:
        "Legal notice for alexandrefernandes.dev, with the information required by § 5 of the German Digital Services Act (DDG).",
      heading: "Legal Notice",
      intro: "Information pursuant to § 5 DDG (German Digital Services Act)",
      country: "Germany",
      contactHeading: "Contact",
      emailLabel: "Email:",
      formLabel: "Contact form:",
      formLink: "Send a message",
      responsibleHeading: "Responsible for the content pursuant to § 18 (2) MStV",
      responsibleText: "Alexandre Rodrigues Fernandes, address as above.",
      back: "Back to home",
    },
    privacy: {
      title: "Privacy Policy",
      description:
        "Privacy policy for alexandrefernandes.dev: which data is processed when you visit the site or get in touch.",
      heading: "Privacy Policy",
      intro:
        "This privacy policy explains which personal data is processed when you visit this website, for what purpose and on what legal basis. This website sets no cookies and does not use your browser's local storage.",
      translationNote: "This English version is a translation for convenience. The German version is legally binding.",
      translationLink: "Read the German version",
      controllerHeading: "Controller",
      emailLabel: "Email:",
      sections: [
        {
          heading: "Hosting and server log files",
          paragraphs: [
            "This website is hosted by Vercel Inc. (USA). Whenever a page is requested, Vercel automatically processes technical data sent by your browser: IP address, date and time, the page requested, referrer URL, browser and operating system. This data is needed to deliver the website and keep it secure.",
            "The legal basis is Art. 6(1)(f) GDPR; my legitimate interest is providing the website securely and reliably. How long these logs are kept is determined by Vercel.",
          ],
          links: [{ label: "Vercel privacy policy", href: "https://vercel.com/legal/privacy-policy" }],
        },
        {
          heading: "Performance measurement (Vercel Speed Insights)",
          paragraphs: [
            "To measure how fast the website loads, I use Vercel Speed Insights. For each measurement it records the page visited, browser, operating system, device type, connection speed, country and loading metrics (Web Vitals). The data is not linked to individual visitors or IP addresses, and no cookies are set.",
            "The legal basis is Art. 6(1)(f) GDPR; my legitimate interest is a fast, technically sound website.",
          ],
          links: [
            { label: "Speed Insights privacy information", href: "https://vercel.com/docs/speed-insights/privacy-policy" },
          ],
        },
        {
          heading: "Visitor statistics (Umami)",
          paragraphs: [
            "For statistics about visits I use Umami Cloud (Umami Software, Inc., USA). Umami works without cookies and stores nothing on your device. It records the page visited, referrer URL, browser, operating system, device type, screen size, language and approximate location. Your IP address is only used to determine that approximate location and is not stored, and visits are not linked to you as a person.",
            "The legal basis is Art. 6(1)(f) GDPR; my legitimate interest is understanding which content on my website is read.",
          ],
          links: [{ label: "Umami privacy policy", href: "https://umami.is/privacy" }],
        },
        {
          heading: "Contact form",
          paragraphs: [
            "If you write to me through the contact form, your name, email address and message are sent via a Cloudflare Worker (Cloudflare, Inc., USA) to the email service Resend (Resend, Inc., USA) and delivered to me by email to a Microsoft Outlook.com mailbox. The data is used only to handle your enquiry.",
            "The legal basis is Art. 6(1)(b) GDPR where your enquiry concerns a job or another contract, and Art. 6(1)(f) GDPR otherwise (legitimate interest in answering enquiries). I delete your message once it has been dealt with, unless statutory retention obligations apply.",
          ],
          links: [
            { label: "Cloudflare privacy policy", href: "https://www.cloudflare.com/privacypolicy/" },
            { label: "Resend privacy policy", href: "https://resend.com/legal/privacy-policy" },
            { label: "Microsoft privacy statement", href: "https://privacy.microsoft.com/privacystatement" },
          ],
        },
        {
          heading: "Contact by email",
          paragraphs: [
            `If you email me at ${email}, your message is stored by my email provider Hostinger (Hostinger International Ltd., Cyprus) and used only to handle your enquiry. The legal basis and retention period are the same as for the contact form.`,
          ],
          links: [{ label: "Hostinger privacy policy", href: "https://www.hostinger.com/legal/privacy-policy" }],
        },
        {
          heading: "Fonts and 3D graphics",
          paragraphs: [
            "Fonts and the 3D graphic on the home page are loaded from this website's own server. No data is sent to Google or other third parties for them.",
          ],
        },
        {
          heading: "Transfers to third countries",
          paragraphs: [
            "Vercel, Umami, Cloudflare, Resend and Microsoft are based in the USA or process data there. Transfers rely on the European Commission's adequacy decision for the EU-US Data Privacy Framework where the provider is certified under it, and otherwise on the EU Standard Contractual Clauses (Art. 46(2)(c) GDPR).",
          ],
        },
        {
          heading: "Your rights",
          paragraphs: [
            "You have the right of access (Art. 15 GDPR), rectification (Art. 16), erasure (Art. 17), restriction of processing (Art. 18) and data portability (Art. 20). Where processing is based on Art. 6(1)(f) GDPR, you can object to it under Art. 21 GDPR. An informal email is enough.",
            "You also have the right to lodge a complaint with a data protection supervisory authority (Art. 77 GDPR), for example the Independent Centre for Privacy Protection Schleswig-Holstein (ULD).",
          ],
          links: [{ label: "ULD Schleswig-Holstein", href: "https://www.datenschutzzentrum.de/" }],
        },
      ],
      updated: "Last updated: October 2026",
      back: "Back to home",
    },
  },

  de: {
    meta: {
      title: "Alexandre Fernandes — AI/ML Software Engineer, Raum Hamburg",
      description:
        "AI/ML Software Engineer im Raum Hamburg. Ich entwickle RAG-, Text-to-SQL- und LLM-Systeme mit Python, LangChain und FastAPI. Offen für Festanstellungen.",
    },
    og: {
      alt: "Alexandre Fernandes — AI/ML Software Engineer",
      eyebrow: "alexandrefernandes.dev",
      title: "Alexandre Fernandes",
      subtitle: "AI/ML Software Engineer · RAG, LLMs & Full Stack",
      footer: "Raum Hamburg",
      projectEyebrow: "Fallstudie · Alexandre Fernandes",
    },
    landing: { role: "Software Engineer", cta: "Portfolio ansehen" },
    nav: [
      { id: "about-me", label: "Über mich" },
      { id: "experience", label: "Erfahrung" },
      { id: "projects", label: "Projekte" },
      { id: "contact", label: "Kontakt" },
    ],
    languageSwitch: {
      href: "/",
      hrefLang: "en",
      label: "EN",
      ariaLabel: "View this page in English",
    },
    hero: {
      role: "Software Engineer | AI/ML & Full Stack",
      intro:
        "Ich entwickle LLM-basierte Systeme und Full-Stack-Anwendungen. Ich lebe im Raum Hamburg und suche eine Stelle im Bereich AI/ML.",
      viewProjects: "Projekte ansehen",
      getInTouch: "Kontakt aufnehmen",
      scroll: "Weiter nach unten",
      scrollLabel: "Nach unten scrollen",
    },
    technologies: { heading: "Technologien" },
    about: {
      heading: "Über mich",
      lead: "Ich entwickle KI-gestützte Systeme und Full-Stack-Anwendungen – von LLM-Pipelines bis zu produktiven Web-Apps.",
      paragraphs: [
        "Ich bin Software Engineer mit Schwerpunkt AI/ML. In meinem Praktikum bei Geodouro habe ich einen vollständig lokal laufenden RAG- und Text-to-SQL-Assistenten für drei PostgreSQL-Datenbanken mit über 500 Tabellen gebaut. Mit Qwen2.5 7B, hybrider Suche und einer LLM-as-Judge-Evaluierung stieg die Antwortgenauigkeit von 13\u00a0% auf 77\u00a0%.",
        "Meine Projekte reichen von Computer Vision (CNN und Kennzeichenerkennung für ein Smart-Parking-System) über LLM- und Sprachanwendungen bis zu Full-Stack-Plattformen, die von echten Teams genutzt werden. Ich wähle das passende Werkzeug für das Problem: Python für AI/ML, React und Node.js für das Web, FastAPI für APIs.",
        "Im Juni 2026 habe ich mein Informatikstudium an der ESTGV/IPV in Portugal abgeschlossen. Heute lebe ich in Schleswig, in Pendelentfernung zu Hamburg. Als EU-Bürger suche ich eine Vollzeitstelle als Software Engineer (AI/ML). Nebenbei baue ich Zyllo, ein LLM-gestütztes SaaS für den E-Commerce, und lerne Deutsch (A1).",
      ],
      resume: "Lebenslauf herunterladen",
      resumeLabel: "Lebenslauf als PDF herunterladen (auf Englisch)",
    },
    stats: [
      { label: "Projekte", value: "5+" },
      { label: "Technologien", value: "10+" },
      { label: "Erfahrung", value: "2 Jahre" },
      { label: "Lernen", value: "Immer" },
    ],
    experience: {
      heading: "Erfahrung & Ausbildung",
      caseStudy: "Zur Fallstudie",
      items: [
        {
          title: "AI Engineer (Praktikum) — Geodouro",
          period: "Feb. 2026 – Juni 2026",
          description:
            "Entwicklung eines produktiven RAG- und Text-to-SQL-Assistenten für SIGP, die Agrarmanagement-Plattform von Geodouro. Statt eines frei agierenden ReAct-Agenten setzte ich auf eine kontrollierte Pipeline (hybride BM25- und MMR-Suche, Schema-Pruning, dynamisches Few-Shot-Prompting, SQL-Validierung mit Selbstkorrektur) über drei PostgreSQL-Datenbanken mit über 500 Tabellen. Die Genauigkeit auf dem Golden Set des Unternehmens stieg von 13\u00a0% auf 77\u00a0%; das lokal laufende Qwen2.5 7B übertraf dabei ein 14B-Modell.",
          tech: ["Python", "LangChain", "LangGraph", "FastAPI", "ChromaDB", "Qwen2.5", "PostgreSQL", "Langfuse"],
          slug: "rag-sql-chatbot",
        },
        {
          title: "B.Sc. Informatik — ESTGV, Instituto Politécnico de Viseu",
          period: "Okt. 2023 – Juni 2026",
          description:
            "Abschluss im Juni 2026 mit der Endnote 15/20 (deutsche Note 2,5). Abschlusspraktikum und -projekt mit 20/20 bewertet (deutsche Note 1,0).",
          tech: [],
        },
      ],
    },
    projects: {
      heading: "Projekte",
      caseStudy: "Zur Fallstudie",
      inProgress: "In Arbeit",
      githubLabel: (title) => `${title} auf GitHub ansehen`,
    },
    projectPage: {
      back: "Alle Projekte",
      problem: "Problem",
      solution: "Lösung",
      stack: "Tech-Stack",
      results: "Ergebnisse",
      repo: "Quellcode",
      demo: "Live-Demo",
      report: "Projektbericht (PDF, Portugiesisch)",
      more: "Weitere Projekte",
      ctaTitle: "Interesse an einer Zusammenarbeit?",
      ctaText: "Ich suche eine Vollzeitstelle als Software Engineer (AI/ML) in Hamburg oder remote.",
      ctaButton: "Kontakt aufnehmen",
      breadcrumbHome: "Startseite",
    },
    contact: {
      heading: "Kontakt",
      subtitle: "Sie haben eine offene Stelle, ein Projekt oder eine Frage? Schreiben Sie mir!",
      name: "Name",
      nameLabel: "Ihr Name",
      email: "E-Mail",
      emailLabel: "Ihre E-Mail-Adresse",
      message: "Nachricht",
      messageLabel: "Ihre Nachricht",
      send: "Nachricht senden",
      sending: "Wird gesendet …",
      success: "Nachricht erfolgreich gesendet!",
      error: "Senden fehlgeschlagen. Bitte versuchen Sie es erneut.",
      privacyNote: "Ihre Angaben werden nur zur Beantwortung Ihrer Nachricht verwendet. Details in der",
      privacyLink: "Datenschutzerklärung",
      connect: "Vernetzen wir uns",
      locationLabel: "Standort:",
      location: "Schleswig · in Pendelentfernung zu Hamburg",
      statusLabel: "Status:",
      status: "Offen für Vollzeitstellen als Software Engineer (AI/ML) · Ab sofort verfügbar",
    },
    footer: {
      role: "Software Engineer | AI/ML & Full Stack",
      navLabel: "Footer-Navigation",
      about: "Über mich",
      projects: "Projekte",
      contact: "Kontakt",
      rights: "Alle Rechte vorbehalten.",
      legal: "Impressum",
      privacy: "Datenschutz",
      backToTop: "Nach oben",
      backToTopLabel: "Zurück zum Seitenanfang",
    },
    legal: {
      title: "Impressum",
      description: "Impressum von alexandrefernandes.dev mit den Angaben gemäß § 5 DDG.",
      heading: "Impressum",
      intro: "Angaben gemäß § 5 DDG",
      country: "Deutschland",
      contactHeading: "Kontakt",
      emailLabel: "E-Mail:",
      formLabel: "Kontaktformular:",
      formLink: "Nachricht senden",
      responsibleHeading: "Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV",
      responsibleText: "Alexandre Rodrigues Fernandes, Anschrift wie oben.",
      back: "Zur Startseite",
    },
    privacy: {
      title: "Datenschutzerklärung",
      description:
        "Datenschutzerklärung von alexandrefernandes.dev: welche Daten beim Besuch der Website und bei einer Kontaktaufnahme verarbeitet werden.",
      heading: "Datenschutzerklärung",
      intro:
        "Diese Datenschutzerklärung informiert Sie darüber, welche personenbezogenen Daten beim Besuch dieser Website verarbeitet werden, zu welchem Zweck und auf welcher Rechtsgrundlage. Diese Website setzt keine Cookies und nutzt keinen lokalen Speicher (Local Storage) Ihres Browsers.",
      controllerHeading: "Verantwortlicher",
      emailLabel: "E-Mail:",
      sections: [
        {
          heading: "Hosting und Server-Logfiles",
          paragraphs: [
            "Diese Website wird bei Vercel Inc. (USA) gehostet. Bei jedem Seitenaufruf verarbeitet Vercel automatisch technische Daten, die Ihr Browser übermittelt: IP-Adresse, Datum und Uhrzeit, aufgerufene Seite, Referrer-URL, Browser und Betriebssystem. Diese Daten sind erforderlich, um die Website auszuliefern und ihre Sicherheit zu gewährleisten.",
            "Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO; mein berechtigtes Interesse liegt in einer sicheren und zuverlässigen Bereitstellung der Website. Die Speicherdauer der Logfiles richtet sich nach den Vorgaben von Vercel.",
          ],
          links: [{ label: "Datenschutzerklärung von Vercel", href: "https://vercel.com/legal/privacy-policy" }],
        },
        {
          heading: "Messung der Ladezeiten (Vercel Speed Insights)",
          paragraphs: [
            "Um die Ladegeschwindigkeit der Website zu messen, nutze ich Vercel Speed Insights. Pro Messung werden die aufgerufene Seite, Browser, Betriebssystem, Gerätetyp, Verbindungsgeschwindigkeit, Land und Ladezeitwerte (Web Vitals) erfasst. Die Daten werden keinen einzelnen Besuchern oder IP-Adressen zugeordnet, und es werden keine Cookies gesetzt.",
            "Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO; mein berechtigtes Interesse liegt in einer schnellen, technisch fehlerfreien Website.",
          ],
          links: [
            { label: "Datenschutzhinweise zu Speed Insights", href: "https://vercel.com/docs/speed-insights/privacy-policy" },
          ],
        },
        {
          heading: "Reichweitenmessung mit Umami",
          paragraphs: [
            "Zur statistischen Auswertung der Besuche nutze ich Umami Cloud (Umami Software, Inc., USA). Umami arbeitet ohne Cookies und speichert nichts auf Ihrem Gerät. Erfasst werden die aufgerufene Seite, die Referrer-URL, Browser, Betriebssystem, Gerätetyp, Bildschirmgröße, Sprache und der ungefähre Standort. Ihre IP-Adresse wird nur zur Bestimmung dieses ungefähren Standorts verwendet und nicht gespeichert; Besuche werden nicht Ihrer Person zugeordnet.",
            "Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO; mein berechtigtes Interesse liegt darin zu verstehen, welche Inhalte meiner Website gelesen werden.",
          ],
          links: [{ label: "Datenschutzerklärung von Umami", href: "https://umami.is/privacy" }],
        },
        {
          heading: "Kontaktformular",
          paragraphs: [
            "Wenn Sie mir über das Kontaktformular schreiben, werden Ihr Name, Ihre E-Mail-Adresse und Ihre Nachricht über einen Cloudflare Worker (Cloudflare, Inc., USA) an den E-Mail-Dienst Resend (Resend, Inc., USA) übermittelt und mir per E-Mail an ein Postfach bei Microsoft Outlook.com zugestellt. Die Daten werden nur zur Bearbeitung Ihrer Anfrage verwendet.",
            "Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, wenn Ihre Anfrage eine Stelle oder einen anderen Vertrag betrifft, ansonsten Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Beantwortung von Anfragen). Ich lösche Ihre Nachricht, sobald sie erledigt ist und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.",
          ],
          links: [
            { label: "Datenschutzerklärung von Cloudflare", href: "https://www.cloudflare.com/privacypolicy/" },
            { label: "Datenschutzerklärung von Resend", href: "https://resend.com/legal/privacy-policy" },
            { label: "Datenschutzerklärung von Microsoft", href: "https://privacy.microsoft.com/de-de/privacystatement" },
          ],
        },
        {
          heading: "Kontakt per E-Mail",
          paragraphs: [
            `Wenn Sie mir eine E-Mail an ${email} schreiben, wird Ihre Nachricht bei meinem E-Mail-Anbieter Hostinger (Hostinger International Ltd., Zypern) gespeichert und nur zur Bearbeitung Ihrer Anfrage verwendet. Rechtsgrundlage und Speicherdauer entsprechen denen beim Kontaktformular.`,
          ],
          links: [{ label: "Datenschutzerklärung von Hostinger", href: "https://www.hostinger.com/legal/privacy-policy" }],
        },
        {
          heading: "Schriftarten und 3D-Grafik",
          paragraphs: [
            "Die Schriftarten und die 3D-Grafik auf der Startseite werden direkt vom Server dieser Website geladen. Dafür werden keine Daten an Google oder andere Dritte übermittelt.",
          ],
        },
        {
          heading: "Übermittlung in Drittländer",
          paragraphs: [
            "Vercel, Umami, Cloudflare, Resend und Microsoft haben ihren Sitz in den USA oder verarbeiten Daten dort. Die Übermittlung erfolgt auf Grundlage des Angemessenheitsbeschlusses der EU-Kommission zum EU-US Data Privacy Framework, soweit der Anbieter danach zertifiziert ist, und im Übrigen auf Grundlage der EU-Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO).",
          ],
        },
        {
          heading: "Ihre Rechte",
          paragraphs: [
            "Sie haben das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung (Art. 18) und Datenübertragbarkeit (Art. 20). Soweit eine Verarbeitung auf Art. 6 Abs. 1 lit. f DSGVO beruht, können Sie ihr nach Art. 21 DSGVO widersprechen. Eine formlose E-Mail genügt.",
            "Außerdem haben Sie das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren (Art. 77 DSGVO), zum Beispiel beim Unabhängigen Landeszentrum für Datenschutz Schleswig-Holstein (ULD).",
          ],
          links: [{ label: "ULD Schleswig-Holstein", href: "https://www.datenschutzzentrum.de/" }],
        },
      ],
      updated: "Stand: Oktober 2026",
      back: "Zur Startseite",
    },
  },
};
